import { applyRouteSettings, settingsForRoute } from "./RouteSettings.ts";
import { findBySlug } from "../Shared/Seo/routeSeo.ts";
import type { PackActivity, PackSpec } from "../Shared/Seo/routeSeo.ts";
import type { ISettingsProps } from "./Models.ts";
import type { SheetSpec } from "./PdfGenerator/sheetSpec.ts";
import { clampPerPageToOptions } from "./Settings/perPageOptions.ts";

/**
 * A substitute pack (ADR 0083) as a list of sheets: a cover, then each activity, then (because every
 * activity has its key on) each activity's key, which is the order `Pages` already puts a multi-
 * document set in - every worksheet page first, then every key, in the same order.
 *
 * Every activity is an ordinary sheet from an existing page: its base settings are that page's own
 * (`settingsForRoute`), then whatever the pack's manifest entry overrides. Nothing here generates
 * a problem. That is what lets a teacher follow an activity's link to its generator page and change
 * it, and what keeps the pack honest as those generators change.
 *
 * Seeds follow the differentiation and class-set pattern: activity `i` draws from `${seed}:${i}` and
 * prints `${seed}-${i}`, so the pack rebuilds from `seed` alone. Typing an activity's own footer ID
 * into Rebuild on its generator page gives back that activity's base sheet, not the pack; the pack's
 * ID is the one on the cover (ADR 0083 §5).
 */

const ORDINALS: Record<number, string> = { 1: "1st", 2: "2nd", 3: "3rd" };

/** "3rd grade". Grades 4 and up are "4th", "5th", and so on. */
export function gradeName(grade: number): string {
    return `${ORDINALS[grade] ?? `${grade}th`} grade`;
}

/**
 * One activity of a pack as a whole sheet with its key: the page's own settings, then the pack's
 * overrides, pinned to one page. Shared by the substitute packs and by the catch-up pack (ADR 0086 §5),
 * which is the same thing built from the pages a teacher chose.
 */
export function wholePageSettings(activity: PackActivity, overrides: Record<string, unknown> = {}): ISettingsProps {
    const base = findBySlug(activity.slug);
    if (!base) {
        throw new Error(`Pack: no page at ${activity.slug}`);
    }
    const settings = applyRouteSettings(settingsForRoute(base), { ...(activity.settings ?? {}), ...overrides });
    // One page of each, whole, with its key: a substitute copies a sheet, not a set of options.
    settings.NumberOfPages = 1;
    settings.HalfPage = false;
    settings.AnswerPages = true;
    settings.Timed = false;
    settings.DifferentiationSet = false;
    // The generator page pulls its per-page count down to what the format offers when the control
    // renders (30 becomes 8 on a word-problem page); a pack renders no control, so it is done here.
    // Left alone, a word-problem activity printed four pages (the page-count sweep found it).
    clampPerPageToOptions(settings);
    return settings;
}

export function subPackSheets(pack: PackSpec, seed: string): SheetSpec[] {
    const activities = pack.activities.map((activity, index) => ({
        // The cover says what each page is in the pack's own words: "Spiral Review Worksheets" tells a
        // substitute nothing, "A mix of two-digit sums, coins and the time" does.
        title: activity.blurb,
        settings: wholePageSettings(activity),
        index,
    }));

    const count = activities.length;
    const cover: SheetSpec = {
        // Not read: a cover draws no problems.
        settings: activities[0].settings,
        seed,
        footerSeed: seed,
        cover: {
            grade: gradeName(pack.grade),
            reviews: pack.reviews,
            id: seed,
            activities: activities.map((activity) => activity.title),
            copyPages: `2 to ${count + 1}`,
            keyPages: `${count + 2} to ${2 * count + 1}`,
        },
    };

    return [
        cover,
        ...activities.map(({ settings, index }) => ({
            settings,
            seed: `${seed}:${index}`,
            footerSeed: `${seed}-${index}`,
        })),
    ];
}
